import type { PlaybooksDict } from '../en/playbooks';

export const playbooks: PlaybooksDict = {
  page: {
    title: 'Playbooks',
    subtitle: 'Hantera dina playbooks och regler',
    createPlaybook: 'Skapa Playbook',
    import: 'Importera',
    createWithAI: 'Skapa med AI',
    loadFailed: 'Det gick inte att läsa in playbooks',
    deleteSuccess: 'Playbook raderad',
    deleteFailed: 'Det gick inte att radera playbook',
  },
  tabs: {
    playbooks: 'Playbooks',
    workflows: 'Arbetsflöden',
  },
  workflows: {
    tab: 'Arbetsflöden',
    title: 'Arbetsflöden',
    description: 'Sparade uppgifter som ni kör igen, till exempel "Granska ett leverantörsavtal mot vår standard". Kör dem från chatten med /.',
    create: 'Nytt arbetsflöde',
    empty: 'Inga arbetsflöden ännu',
    emptyHint: 'Spara instruktioner ni ofta ger QAgent och dela dem med er organisation.',
    name: 'Namn',
    namePlaceholder: 't.ex. Granskning av SaaS-leverantör',
    descriptionLabel: 'Beskrivning (valfritt)',
    instructions: 'Instruktioner',
    instructionsHint: 'Vad QAgent ska göra. Skriv {{document}} där det valda dokumentet ska stå.',
    instructionsPlaceholder: 'Granska {{document}} mot vår SaaS-standard. Fokusera på ansvarsbegränsning, dataskydd och uppsägning. Lista avvikelser efter risk.',
    kind: 'Körs som',
    kinds: {
      prompt: 'Bara instruktioner',
      review: 'Granska ett dokument',
      compare: 'Jämför med ett standardavtal',
      summarize: 'Sammanfatta ett dokument',
    },
    playbook: 'Playbook (valfritt)',
    standardAgreement: 'Standardavtal (valfritt)',
    none: 'Ingen',
    visibility: 'Vem kan använda det',
    private: 'Bara jag',
    organization: 'Alla i min organisation',
    shared: 'Delat',
    by: (name: string) => `av ${name}`,
    edit: 'Redigera',
    delete: 'Radera',
    save: 'Spara',
    cancel: 'Avbryt',
    saved: 'Arbetsflödet har sparats',
    deleted: 'Arbetsflödet har raderats',
    failed: 'Något gick fel',
    deleteTitle: 'Radera arbetsflöde',
    deleteDescription: (name: string) => `Radera "${name}"? De som det delas med kan inte längre köra det.`,
  },
  sections: {
    privateTitle: 'Privata Playbooks',
    privateDescription: 'Playbooks som endast är synliga för dig',
    organizationTitle: 'Organisationens Playbooks',
    organizationDescription: 'Playbooks som delas inom din organisation',
  },
  table: {
    name: 'Namn',
    agreementType: 'Avtalstyp',
    rules: 'Regler',
    visibility: 'Synlighet',
    created: 'Skapad',
    activeBadge: (active: number, total: number) => `${active} / ${total} aktiva`,
    approvedBadge: 'Godkänd',
    private: 'Privat',
    organization: 'Organisation',
    openMenu: 'Öppna meny',
    actions: 'Åtgärder',
    viewDetails: 'Visa detaljer',
    delete: 'Radera',
    filterPlaceholder: 'Filtrera playbooks...',
    noPrivate: 'Inga privata playbooks hittades.',
    noOrganization: 'Inga playbooks för organisationen hittades.',
    loading: 'Laddar...',
    previous: 'Föregående',
    next: 'Nästa',
  },
  deleteDialog: {
    title: 'Radera Playbook',
    description: (name: string) =>
      `Är du säker på att du vill radera ”${name}”? Denna åtgärd kan inte ångras.`,
    cancel: 'Avbryt',
    confirm: 'Radera',
  },
  detail: {
    loading: 'Laddar playbook...',
    notFound: 'Playbook hittades inte',
    backToPlaybooks: 'Tillbaka till Playbooks',
    hideSource: 'Dölj källa',
    showSource: 'Visa källa',
    edit: 'Redigera',
    delete: 'Radera',
    editTitle: 'Redigera Playbook',
    editDescription: 'Uppdatera playbook-detaljer',
    nameLabel: 'Namn',
    namePlaceholder: 'Playbook-namn',
    agreementTypeLabel: 'Avtalstyp',
    agreementTypePlaceholder: 'Avtalstyp',
    privateLabel: 'Privat Playbook',
    privateOnlyYou: 'Endast du kan se denna playbook',
    privateEveryone: 'Alla i din organisation kan se denna playbook',
    saveChanges: 'Spara ändringar',
    cancel: 'Avbryt',
    nameAndTypeRequired: 'Namn och avtalstyp krävs',
    updateSuccess: 'Playbook uppdaterad',
    updateFailed: 'Det gick inte att uppdatera playbook',
    deleteSuccess: 'Playbook raderad',
    deleteFailed: 'Det gick inte att radera playbook',
    rulesTitle: 'Regler',
    rulesDescription: 'Granska regler som definierats i denna playbook',
    addRule: 'Lägg till regel',
    noRules: 'Inga regler definierade än.',
    addFirstRule: 'Klicka på ”Lägg till regel” för att skapa din första regel.',
    sourceDocumentTitle: 'Källdokument',
    sourceDocumentDescription: (type: string) => `${type}-dokument`,
    sourceDocumentPages: (count: number) => ` - ${count} sidor`,
    sourceDocumentSheets: (count: number) => ` - ${count} blad`,
    loadingSourceDocument: 'Laddar källdokument...',
    noSourceDocument: 'Inget källdokument tillgängligt',
    loadSourceFileFailed: 'Det gick inte att läsa in källdokumentets fil',
    loadSourceError: 'Fel vid inläsning av källdokument',
  },
  createDialog: {
    title: 'Skapa Playbook',
    description:
      'Skapa en ny playbook manuellt. Du kan lägga till regler efter att du skapat playbooken.',
    nameLabel: 'Namn',
    namePlaceholder: 'Ange playbook-namn',
    agreementTypeLabel: 'Avtalstyp',
    agreementTypePlaceholder: 't.ex. NDA, Anställningsavtal, SaaS-avtal',
    privateLabel: 'Privat Playbook',
    privateOnlyYou: 'Endast du kan se denna playbook',
    privateEveryone: 'Alla i din organisation kan se denna playbook',
    cancel: 'Avbryt',
    creating: 'Skapar...',
    submit: 'Skapa Playbook',
    requiredFields: 'Fyll i alla obligatoriska fält',
    createSuccess: 'Playbook skapad',
    createFailed: 'Det gick inte att skapa playbook',
  },
  aiDialog: {
    title: 'Skapa Playbook med AI',
    description:
      'Beskriv vilken typ av playbook du behöver, så genererar AI:n den med relevanta regler.',
    stages: {
      reading: 'Läser din beskrivning',
      identifying: 'Identifierar avtalstyp',
      drafting: 'Utformar granskningsregler',
      refining: 'Förfinar klausuler',
      almostDone: 'Nästan klar',
    },
    timeHint: 'Detta tar vanligtvis 10-25 sekunder. Ha tålamod.',
    descriptionLabel: 'Beskrivning',
    descriptionPlaceholder:
      'Exempel: Skapa en playbook för granskning av anställningsavtal. Inkludera regler för ersättningsklausuler, uppsägningsvillkor och sekretessavtal.',
    descriptionHint:
      'Var specifik kring avtalstypen och vilka regler du vill inkludera.',
    privateLabel: 'Privat Playbook',
    privateOnlyYou: 'Endast du kan se denna playbook',
    privateEveryone: 'Alla i din organisation kan se denna playbook',
    cancel: 'Avbryt',
    generating: 'Genererar...',
    submit: 'Generera Playbook',
    describeRequired: 'Beskriv den playbook du vill skapa',
    createSuccess: 'Playbook skapad med AI',
    createFailed: 'Det gick inte att skapa playbook med AI',
  },
  importDialog: {
    title: 'Importera Playbook',
    description:
      'Ladda upp en dokumentfil för att automatiskt generera en playbook med AI. Format som stöds: PDF, DOCX, XLSX, XLS, TXT (max 25 MB).',
    fileLabel: 'Dokumentfil',
    dropHint: 'Klicka för att välja en fil eller dra och släpp',
    formatsHint: 'PDF, DOCX, XLSX, XLS, TXT (max 25 MB)',
    selectFile: 'Välj fil',
    longDocumentHint: 'Långa dokument kan ta några minuter. Håll fönstret öppet.',
    uploadingProcessing: 'Laddar upp och bearbetar...',
    privateLabel: 'Privat Playbook',
    privateOnlyYou: 'Endast du kan se denna playbook',
    privateEveryone: 'Alla i din organisation kan se denna playbook',
    cancel: 'Avbryt',
    importing: 'Importerar...',
    submit: 'Importera Playbook',
    fileSizeExceeds: (size: string) =>
      `Filstorleken överskrider gränsen på 25 MB. Din fil är ${size} MB.`,
    invalidFileType: 'Ogiltig filtyp. Format som accepteras: PDF, DOCX, XLSX, XLS, TXT',
    selectFileRequired: 'Välj en fil att importera',
    importSuccess: 'Playbook importerad!',
    importFailed: 'Det gick inte att importera playbook',
    unexpectedError: 'Ett oväntat fel inträffade under importen',
  },
  createRuleDialog: {
    title: 'Skapa Regel',
    description: 'Lägg till en ny regel i denna playbook.',
    nameLabel: 'Regelnamn',
    namePlaceholder: 't.ex. Kontrollera ersättningsklausul',
    instructionsLabel: 'Instruktioner',
    instructionsPlaceholder: 'Beskriv vad denna regel ska kontrollera eller validera...',
    enableCommentsLabel: 'Aktivera kommentarer',
    enableCommentsHint: 'Tillåt AI att lägga till kommentarer vid granskning av denna regel',
    commentGuidelinesLabel: 'Kommentarsriktlinjer (Valfritt)',
    commentGuidelinesPlaceholder:
      'Ange riktlinjer för hur AI ska kommentera denna regel...',
    activeLabel: 'Aktiv Regel',
    activeHint: 'Endast aktiva regler används vid dokumentgranskning',
    cancel: 'Avbryt',
    creating: 'Skapar...',
    submit: 'Skapa Regel',
    requiredFields: 'Fyll i alla obligatoriska fält',
    createSuccess: 'Regel skapad',
    createFailed: 'Det gick inte att skapa regel',
  },
  ruleItem: {
    inactive: 'Inaktiv',
    comments: 'Kommentarer',
    sources: (count: number) => `${count} källa${count > 1 ? 'or' : ''}`,
    nameLabel: 'Namn',
    namePlaceholder: 'Regelnamn',
    instructionsLabel: 'Instruktioner',
    instructionsPlaceholder: 'Regelinstruktioner',
    enableCommentsLabel: 'Aktivera kommentarer',
    enableCommentsHint: 'Tillåt AI att lägga till kommentarer vid granskning',
    commentGuidelinesLabel: 'Kommentarsriktlinjer (Valfritt)',
    commentGuidelinesPlaceholder: 'Riktlinjer för hur AI ska kommentera denna regel',
    activeLabel: 'Aktiv Regel',
    activeHint: 'Endast aktiva regler används vid granskning',
    saveChanges: 'Spara ändringar',
    cancel: 'Avbryt',
    instructionsHeading: 'Instruktioner',
    commentGuidelinesHeading: 'Kommentarsriktlinjer',
    sourceQuotesHeading: 'Källcitat',
    paragraph: (index: number) => `Stycke ${index}`,
    page: (page: number) => `Sida ${page}`,
    sheetCell: (sheet: string, cell: string) => `Blad: ${sheet}, Cell: ${cell}`,
    confidence: (level: string) => `${level} säkerhet`,
    created: (date: string) => `Skapad: ${date}`,
    updated: (date: string) => `Uppdaterad: ${date}`,
    deleteConfirm: 'Är du säker på att du vill radera denna regel?',
    nameAndInstructionsRequired: 'Namn och instruktioner krävs',
    updateSuccess: 'Regel uppdaterad',
    updateFailed: 'Det gick inte att uppdatera regel',
    deleteSuccess: 'Regel raderad',
    deleteFailed: 'Det gick inte att radera regel',
    activated: 'Regel aktiverad',
    deactivated: 'Regel inaktiverad',
  },
  viewer: {
    noSourceDocument: 'Inget källdokument tillgängligt',
    unsupportedFileType: (type: string) => `Filtypen stöds inte: ${type}`,
    errorLoadingPdf: (error: string) => `Fel vid inläsning av PDF: ${error}`,
    loadingPdf: 'Laddar PDF...',
    pageOf: (page: number, total: number) => `Sida ${page} av ${total}`,
    zoomOut: 'Zooma ut',
    zoomIn: 'Zooma in',
    resetZoom: 'Återställ zoom',
    fitToWidth: 'Anpassa till bredd',
    loadingDocument: 'Laddar dokument...',
    loadingSpreadsheet: 'Laddar kalkylblad...',
    error: (error: string) => `Fel: ${error}`,
  },
};
